import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, UserPlus, UserMinus, Search,
    User, Users, Briefcase
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import {
    getTeamCustomRoster,
    searchCustomPeople,
    addToTeamRoster,
    removeFromTeamRoster
} from '../../utils/api';
import toast from 'react-hot-toast';

export default function TeamRosterManagement() {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [personTypeFilter, setPersonTypeFilter] = useState('');

    // Fetch team roster
    const { data: rosterData, isLoading } = useQuery({
        queryKey: ['team-roster', teamId],
        queryFn: () => getTeamCustomRoster(teamId),
        enabled: !!teamId
    });

    // Search custom people
    const { data: searchResults, isLoading: searchLoading } = useQuery({
        queryKey: ['search-custom-people', searchQuery, personTypeFilter],
        queryFn: () => searchCustomPeople(searchQuery, personTypeFilter || undefined),
        enabled: searchQuery.length >= 2
    });

    // Add to roster mutation
    const addMutation = useMutation({
        mutationFn: (customPersonId) => addToTeamRoster(teamId, customPersonId),
        onSuccess: () => {
            toast.success('Person added to roster');
            queryClient.invalidateQueries(['team-roster', teamId]);
            setShowAddModal(false);
            setSearchQuery('');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to add to roster');
        }
    });

    // Remove from roster mutation
    const removeMutation = useMutation({
        mutationFn: (personId) => removeFromTeamRoster(teamId, personId),
        onSuccess: () => {
            toast.success('Person removed from roster');
            queryClient.invalidateQueries(['team-roster', teamId]);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to remove from roster');
        }
    });

    const roster = rosterData?.data?.roster || [];
    const team = rosterData?.data?.team;
    const players = roster.filter(r => r.person?.personType === 'player' && r.isActive);
    const staff = roster.filter(r => r.person?.personType === 'staff' && r.isActive);

    // Get person IDs already in roster
    const rosterPersonIds = new Set(roster.map(r => r.person?._id));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/basketball/teams')}
                    className="p-2 hover:bg-accent-light dark:hover:bg-accent-dark rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold">
                        {team?.name || 'Team'} Roster
                    </h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manage custom roster for this team
                    </p>
                </div>
                <Button onClick={() => setShowAddModal(true)}>
                    <UserPlus size={18} className="mr-2" />
                    Add Person
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : (
                <>
                    {/* Players Section */}
                    <Card>
                        <div className="flex items-center gap-2 mb-4">
                            <Users size={20} className="text-blue-500" />
                            <h2 className="text-xl font-semibold">Players ({players.length})</h2>
                        </div>

                        {players.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {players.map((entry) => (
                                    <div
                                        key={entry.entryId}
                                        className="flex items-center gap-3 p-3 bg-accent-light dark:bg-accent-dark rounded-lg"
                                    >
                                        {entry.person.imageUrl ? (
                                            <img
                                                src={entry.person.imageUrl}
                                                alt={`${entry.person.firstName} ${entry.person.lastName}`}
                                                className="w-12 h-12 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <User size={24} className="text-gray-400" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">
                                                {entry.person.firstName} {entry.person.lastName}
                                            </div>
                                            <div className="text-sm text-muted-light dark:text-muted-dark flex items-center gap-2">
                                                {entry.person.shirtNumber && (
                                                    <span>#{entry.person.shirtNumber}</span>
                                                )}
                                                {entry.person.position && (
                                                    <Badge variant="info" className="text-xs">
                                                        {entry.person.position}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeMutation.mutate(entry.person._id)}
                                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            disabled={removeMutation.isPending}
                                        >
                                            <UserMinus size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-light dark:text-muted-dark text-center py-8">
                                No players added yet
                            </p>
                        )}
                    </Card>

                    {/* Staff Section */}
                    <Card>
                        <div className="flex items-center gap-2 mb-4">
                            <Briefcase size={20} className="text-purple-500" />
                            <h2 className="text-xl font-semibold">Staff ({staff.length})</h2>
                        </div>

                        {staff.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {staff.map((entry) => (
                                    <div
                                        key={entry.entryId}
                                        className="flex items-center gap-3 p-3 bg-accent-light dark:bg-accent-dark rounded-lg"
                                    >
                                        {entry.person.imageUrl ? (
                                            <img
                                                src={entry.person.imageUrl}
                                                alt={`${entry.person.firstName} ${entry.person.lastName}`}
                                                className="w-12 h-12 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <User size={24} className="text-gray-400" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">
                                                {entry.person.firstName} {entry.person.lastName}
                                            </div>
                                            {entry.person.staffRole && (
                                                <Badge variant="purple" className="text-xs">
                                                    {entry.person.staffRole}
                                                </Badge>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => removeMutation.mutate(entry.person._id)}
                                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            disabled={removeMutation.isPending}
                                        >
                                            <UserMinus size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-light dark:text-muted-dark text-center py-8">
                                No staff added yet
                            </p>
                        )}
                    </Card>
                </>
            )}

            {/* Add Person Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setSearchQuery('');
                }}
                title="Add Person to Roster"
                size="medium"
            >
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search custom people..."
                                className="pl-10"
                            />
                        </div>
                        <select
                            value={personTypeFilter}
                            onChange={(e) => setPersonTypeFilter(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark"
                        >
                            <option value="">All Types</option>
                            <option value="player">Players</option>
                            <option value="staff">Staff</option>
                        </select>
                    </div>

                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Type at least 2 characters to search. Only custom people can be added to rosters.
                    </p>

                    {searchLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                    ) : searchResults?.data?.length > 0 ? (
                        <div className="max-h-96 overflow-y-auto space-y-2">
                            {searchResults.data.map((person) => {
                                const alreadyInRoster = rosterPersonIds.has(person._id);

                                return (
                                    <div
                                        key={person._id}
                                        className={`flex items-center gap-3 p-3 rounded-lg border ${alreadyInRoster
                                                ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
                                                : 'bg-white dark:bg-gray-900 border-border-light dark:border-border-dark hover:bg-accent-light dark:hover:bg-accent-dark'
                                            }`}
                                    >
                                        {person.imageUrl ? (
                                            <img
                                                src={person.imageUrl}
                                                alt={`${person.firstName} ${person.lastName}`}
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <User size={20} className="text-gray-400" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">
                                                {person.firstName} {person.lastName}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant={person.personType === 'player' ? 'info' : 'purple'}
                                                    className="text-xs"
                                                >
                                                    {person.personType}
                                                </Badge>
                                                {person.position && (
                                                    <span className="text-xs text-muted-light dark:text-muted-dark">
                                                        {person.position}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {alreadyInRoster ? (
                                            <Badge variant="success" className="text-xs">
                                                In Roster
                                            </Badge>
                                        ) : (
                                            <Button
                                                size="sm"
                                                onClick={() => addMutation.mutate(person._id)}
                                                disabled={addMutation.isPending}
                                            >
                                                <UserPlus size={14} className="mr-1" />
                                                Add
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : searchQuery.length >= 2 ? (
                        <div className="text-center py-8">
                            <p className="text-muted-light dark:text-muted-dark">
                                No custom people found
                            </p>
                            <button
                                onClick={() => navigate('/basketball/people')}
                                className="text-blue-600 hover:text-blue-700 text-sm mt-2"
                            >
                                Create a new custom person
                            </button>
                        </div>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
}

